import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-16">
      <Helmet>
        <title>Privacy Policy | VortexWebCo</title>
        <meta name="description" content="Privacy Policy for Vortex Web Co - Learn how we collect, use, and protect your information." />
        <meta property="og:title" content="Privacy Policy | VortexWebCo" />
        <meta property="og:description" content="Privacy Policy for Vortex Web Co - Learn how we collect, use, and protect your information." />
        <meta property="og:url" content="https://vortexweb.co/privacy" />
        <meta property="og:site_name" content="vortexweb.co" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#e62020] transition-colors font-mono text-sm"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Shield size={20} className="text-[#e62020]" />
            <span className="text-[#e62020] font-mono text-sm tracking-[0.3em] uppercase">
              // LEGAL
            </span>
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold uppercase tracking-wide text-white mb-4"
            style={{ fontFamily: 'Anton, sans-serif' }}
            data-testid="privacy-title"
          >
            Privacy <span className="text-[#e62020]">Policy</span>
          </h1>
          <p className="text-gray-500 font-mono text-sm">
            Vortex Web Co | vortexweb.co | Last Updated: March 12, 2026
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-invert max-w-none"
        >
          <div className="space-y-8 text-gray-400">
            {/* Section 1 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">01</span>
                Introduction
              </h2>
              <p className="leading-relaxed">
                Welcome to Vortex Web Co ("we," "us," or "our"). We are a web design agency located in the United States. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website vortexweb.co or purchase our services.
              </p>
            </section>

            {/* Section 2 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">02</span>
                Information We Collect
              </h2>
              <p className="mb-4">We may collect the following types of information:</p>
              <ul className="space-y-3 list-none pl-0">
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020] mt-1">›</span>
                  <span><strong className="text-white">Personal Information:</strong> Name, email address, phone number, and business name when you contact us or sign up for our services.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020] mt-1">›</span>
                  <span><strong className="text-white">Payment Information:</strong> Billing details processed securely through Stripe. We do not store your full credit card or bank account information on our servers.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020] mt-1">›</span>
                  <span><strong className="text-white">Usage Data:</strong> Information about how you interact with our website, including IP address, browser type, and pages visited.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020] mt-1">›</span>
                  <span><strong className="text-white">Communications:</strong> Emails or messages you send us.</span>
                </li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">03</span>
                How We Use Your Information
              </h2>
              <p className="mb-4">We use the information we collect to:</p>
              <ul className="space-y-2 list-none pl-0">
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020]">›</span>
                  <span>Provide, operate, and maintain our web design services</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020]">›</span>
                  <span>Process payments and manage your membership or subscription plan</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020]">›</span>
                  <span>Communicate with you about your project, invoices, or account</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020]">›</span>
                  <span>Improve our website and services</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020]">›</span>
                  <span>Comply with legal obligations</span>
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">04</span>
                Payment Processing
              </h2>
              <p className="leading-relaxed">
                We use Stripe to process payments for our services, which include monthly plans starting at $50/month and annual plans at $500/year. Stripe may collect and store your financial data in accordance with their own privacy policy. We do not store payment card details on our servers. All payment data is stored in the United States.
              </p>
            </section>

            {/* Section 5 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">05</span>
                Sharing Your Information
              </h2>
              <p className="mb-4">We do not sell your personal information. We may share your information with:</p>
              <ul className="space-y-3 list-none pl-0">
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020] mt-1">›</span>
                  <span><strong className="text-white">Service Providers:</strong> Third-party vendors such as Stripe (payments) and Resend (email) that help us operate our business.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020] mt-1">›</span>
                  <span><strong className="text-white">Legal Requirements:</strong> If required by law or to protect our rights.</span>
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">06</span>
                Data Storage
              </h2>
              <p className="leading-relaxed">
                All data we collect is stored in the United States. We take reasonable technical and organizational measures to protect your information from unauthorized access or disclosure.
              </p>
            </section>

            {/* Section 7 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">07</span>
                Your Rights
              </h2>
              <p className="mb-4">Depending on your location, you may have the right to:</p>
              <ul className="space-y-2 list-none pl-0">
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020]">›</span>
                  <span>Access the personal information we hold about you</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020]">›</span>
                  <span>Request correction or deletion of your data</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#e62020]">›</span>
                  <span>Opt out of marketing communications</span>
                </li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">08</span>
                Cookies
              </h2>
              <p className="leading-relaxed">
                Our website may use cookies to improve your browsing experience. You can control cookie settings through your browser.
              </p>
            </section>

            {/* Section 9 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">09</span>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children.
              </p>
            </section>

            {/* Section 10 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">10</span>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by updating the date at the top of this page.
              </p>
            </section>

            {/* Section 11 */}
            <section className="bg-[#0d0d0d] border border-white/5 p-6 md:p-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3" style={{ fontFamily: 'Anton, sans-serif' }}>
                <span className="text-[#e62020] font-mono text-sm">11</span>
                Contact Us
              </h2>
              <p className="mb-4">If you have any questions about this Privacy Policy, please contact us:</p>
              <div className="bg-[#0a0a0a] border border-[#e62020]/20 p-4 font-mono text-sm">
                <p className="text-white mb-1">Vortex Web Co</p>
                <p className="text-gray-500">Website: <a href="https://vortexweb.co" className="text-[#e62020] hover:underline">vortexweb.co</a></p>
                <p className="text-gray-500">Email: <a href="mailto:vortexwebco@gmail.com" className="text-[#e62020] hover:underline">vortexwebco@gmail.com</a></p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
